import React, { useEffect, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, DataLabelsPlugin);

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://myssa1.osc-fr1.scalingo.io/api';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString('fr-FR'));

const parseLocalDate = (str) => {
  if (!str) return null;
  const parts = str.split('/');
  if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
  return new Date(str);
};

const getMoisLabel = (offset) => {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return base.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

const getMonthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const getWeekKey = (d) => {
  const tmp = new Date(d); tmp.setHours(0, 0, 0, 0);
  const day = tmp.getDay() || 7; tmp.setDate(tmp.getDate() - day + 1);
  return `${tmp.getFullYear()}-S${String(Math.ceil(
    ((tmp - new Date(tmp.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '00')}`;
};

// ─── Constantes seuils stock fort / stock faible ─────────────────────────────
const RATIO_FORT   = 2.0;
const RATIO_FAIBLE = 0.5;
const STOCK_FORT_ABS_FALLBACK   = 50;
const STOCK_FAIBLE_ABS_FALLBACK =  0;

// ─── Agrégation ──────────────────────────────────────────────────────────────
const aggregateRows = (rows, granularity) => {
  if (granularity === 'jour') return rows.map(r => ({ ...r, _periodKey: r.date }));
  const groups = {};
  rows.forEach((r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return;
    const key = granularity === 'semaine' ? getWeekKey(d) : getMonthKey(d);
    if (!groups[key]) groups[key] = { lastRow: null, sumDepart: 0, sumLivraison: 0, sumPrevision: 0, sumCommandeFerme: 0 };
    const g = groups[key];
    if (!g.lastRow || parseLocalDate(r.date) >= parseLocalDate(g.lastRow.date)) g.lastRow = r;
    const lf = parseFloat(r['livraison fournisseur']); if (!isNaN(lf)) g.sumLivraison += lf;
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) {
      g.sumDepart += dc;
      if (r['type de donnee'] === 'prévision') g.sumPrevision += dc;
      if (r['type de donnee'] === 'commande ferme') g.sumCommandeFerme += dc;
    }
  });
  return Object.entries(groups).filter(([, g]) => g.lastRow).map(([key, g]) => ({
    ...g.lastRow,
    'depart client': g.sumDepart,
    'livraison fournisseur': g.sumLivraison,
    _sumPrevision: g.sumPrevision,
    _sumCommandeFerme: g.sumCommandeFerme,
    _periodKey: key,
  })).sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
};

const formatLabel = (dateStr, granularity) => {
  const d = parseLocalDate(dateStr);
  if (!d || isNaN(d)) return dateStr || '';
  if (granularity === 'jour') return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  if (granularity === 'semaine') return `S${String(Math.ceil(
    ((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - new Date(d.getFullYear(), 0, 1)) / 86400000 + 1) / 7
  )).padStart(2, '0')} ${d.getFullYear()}`;
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

// ─── Statuts & badges ─────────────────────────────────────────────────────────
const STATUT_CFG = {
  'ok':            { label: 'Ok',           bg: '#EAF3DE', color: '#3B6D11', dot: '#4CAF50' },
  'rupture':       { label: 'Rupture',      bg: '#FCEBEB', color: '#A32D2D', dot: '#E53935' },
  'rupture sèche': { label: 'Rupture sèche',bg: '#F7C1C1', color: '#791F1F', dot: '#B71C1C' },
  'stock faible':  { label: 'Stock faible', bg: '#FAEEDA', color: '#854F0B', dot: '#FF9800' },
  'stock fort':    { label: 'Stock fort',   bg: '#E8F4FD', color: '#1565C0', dot: '#1976D2' },
  'hausse':        { label: '↑ Hausse',     bg: '#EAF3DE', color: '#3B6D11', dot: '#4CAF50' },
  'baisse':        { label: '↓ Baisse',     bg: '#FCEBEB', color: '#A32D2D', dot: '#E53935' },
  'stable':        { label: '→ Stable',     bg: '#F3F4F6', color: '#6B7280', dot: '#9E9E9E' },
};

const StatusBadge = ({ statut, small }) => {
  const cfg = STATUT_CFG[statut] ?? { label: statut, bg: '#F3F4F6', color: '#6B7280', dot: '#9E9E9E' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: small ? '2px 8px' : '4px 11px',
      borderRadius: 20, fontSize: small ? 12 : 13, fontWeight: 500,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

const MoisBadge = ({ statut, ruptureInfo }) => {
  const cfg = STATUT_CFG[statut] ?? { label: statut, bg: '#F3F4F6', color: '#6B7280', dot: '#9E9E9E' };
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 500,
        background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
        {cfg.label}
      </span>
      {ruptureInfo && (
        <span title={`Rupture en cours de mois : ${fmtQty(Math.round(ruptureInfo.stockMin))} → ${fmtQty(Math.round(ruptureInfo.stockFin))} fin de mois`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700,
            background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D',
            whiteSpace: 'nowrap', cursor: 'help',
          }}>
          ⚠ rupture {fmtQty(Math.round(ruptureInfo.stockMin))}
        </span>
      )}
    </span>
  );
};

const CmdFrnBadge = ({ cmds }) => {
  if (!cmds || cmds.length === 0) return <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>;
  const hasReport = cmds.some(c => c.statut === 'REPORT');
  const hasInf    = cmds.some(c => c.statut === 'inf +2 semaines');
  const bg    = hasInf ? '#FFF3CD' : hasReport ? '#E8F4FD' : '#EAF3DE';
  const color = hasInf ? '#856404' : hasReport ? '#1565C0' : '#3B6D11';
  const totalQty = cmds.reduce((s, c) => s + (parseFloat(c['Quantité commandée']) || 0), 0);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: bg, color }}>
        {cmds.length} cmde{cmds.length > 1 ? 's' : ''} · {fmtQty(totalQty)} u.
      </span>
    </span>
  );
};

// ─── Calcul graphe ────────────────────────────────────────────────────────────
const buildChartData = (rows, granularity = 'mois') => {
  const agg = aggregateRows(rows, granularity);
  return {
    labels: agg.map((r) => formatLabel(r.date, granularity)),
    datasets: [
      {
        label: 'Stock projeté', type: 'line',
        data: agg.map((r) => Math.round(parseFloat(r['stock projete']) || 0)),
        borderColor: '#0D2140', backgroundColor: 'rgba(13,33,64,0.15)',
        borderWidth: 2.5, fill: false, tension: 0.3, datalabels: { display: false },
      },
      {
        label: 'Stock de sécurité', type: 'line',
        data: new Array(agg.length).fill(Math.round(parseFloat(agg[0]?.['stock mini'] || 0))),
        borderColor: '#E53935', backgroundColor: 'rgba(229,57,53,0.08)',
        borderWidth: 1.5, fill: false, borderDash: [6, 4], datalabels: { display: false },
      },
      {
        label: 'Prévision', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'prévision' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumPrevision || 0)),
        backgroundColor: 'rgba(25,118,210,0.45)', borderColor: '#1976D2',
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: '#1565C0', anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Commande ferme', type: 'bar',
        data: agg.map((r) => granularity === 'jour'
          ? (r['type de donnee'] === 'commande ferme' ? Math.round(parseFloat(r['depart client']) || 0) : 0)
          : Math.round(r._sumCommandeFerme || 0)),
        backgroundColor: 'rgba(229,57,53,0.45)', borderColor: '#E53935',
        borderWidth: 1, stack: 'out', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: '#B71C1C', anchor: 'end', align: 'top', font: { size: 10 } },
      },
      {
        label: 'Livraison fournisseur', type: 'bar',
        data: agg.map((r) => Math.round(parseFloat(r['livraison fournisseur']) || 0)),
        backgroundColor: 'rgba(255,152,0,0.5)', borderColor: '#FF9800',
        borderWidth: 1, stack: 'in', datalabels: { display: ctx => ctx.dataset.data[ctx.dataIndex] !== 0, color: '#E65100', anchor: 'end', align: 'top', font: { size: 10 } },
      },
    ],
  };
};

const chartOptions = {
  responsive: true, maintainAspectRatio: false,
  scales: {
    x: { stacked: false, ticks: { maxRotation: 45, minRotation: 0, font: { size: 11 } }, grid: { color: '#F3F4F6' } },
    y: { stacked: false, ticks: { font: { size: 11 } }, grid: { color: '#F3F4F6' } },
  },
  plugins: {
    legend: { display: true, labels: { font: { size: 12 }, boxWidth: 12 } },
    datalabels: { color: '#374151', font: { weight: 'bold', size: 10 } },
  },
};

// ─── Simulation ───────────────────────────────────────────────────────────────
const buildSimulatedRows = (rows, cmds) => {
  let simRows = rows.map(r => ({ ...r }));

  cmds.forEach(cmd => {
    const origDate   = cmd['Date de livraison'];
    const reportDate = cmd['Report proposé'];
    const statut     = (cmd.statut || '').trim().toUpperCase();
    const qty        = parseFloat(cmd['Quantité commandée']) || 0;

    if (statut === 'ANNULATION') {
      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        const oDate = parseLocalDate(origDate) || new Date(origDate);
        if (!rDate || !oDate || isNaN(rDate) || isNaN(oDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });
    } else if (reportDate && reportDate !== 'ANNULATION') {
      const oDate = parseLocalDate(origDate) || new Date(origDate);
      const nDate = parseLocalDate(reportDate) || new Date(reportDate);
      if (!oDate || !nDate || isNaN(oDate) || isNaN(nDate)) return;

      simRows = simRows.map(r => {
        const rDate = parseLocalDate(r.date);
        if (!rDate || isNaN(rDate)) return r;
        if (rDate.toDateString() === oDate.toDateString() && r['type de donnee'] === 'commande frn') {
          return { ...r, 'livraison fournisseur': Math.max(0, (parseFloat(r['livraison fournisseur']) || 0) - qty) };
        }
        return r;
      });

      const existingIdx = simRows.findIndex(r => {
        const rDate = parseLocalDate(r.date);
        return rDate && !isNaN(rDate) && rDate.toDateString() === nDate.toDateString();
      });
      if (existingIdx >= 0) {
        simRows[existingIdx] = {
          ...simRows[existingIdx],
          'livraison fournisseur': (parseFloat(simRows[existingIdx]['livraison fournisseur']) || 0) + qty,
        };
      } else {
        const refRow = simRows[0] || {};
        const dd = nDate.getDate(), mm = nDate.getMonth() + 1, yyyy = nDate.getFullYear();
        simRows.push({
          ...refRow,
          date: `${String(dd).padStart(2,'0')}/${String(mm).padStart(2,'0')}/${yyyy}`,
          'type de donnee': 'commande frn',
          'livraison fournisseur': qty,
          'depart client': 0,
        });
      }
    }
  });

  simRows.sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
  let stockCourant = parseFloat(simRows[0]?.stock) || 0;
  simRows = simRows.map((r) => {
    const lf = parseFloat(r['livraison fournisseur']) || 0;
    const dc = parseFloat(r['depart client']) || 0;
    stockCourant = stockCourant + lf - dc;
    return { ...r, 'stock projete': stockCourant };
  });

  return simRows;
};

// ─── Calcul stats par item ────────────────────────────────────────────────────
const _now = new Date();
const getMonthPeriod = (offset) => {
  const d = new Date(_now.getFullYear(), _now.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const computeItemStats = (rows) => {
  const now  = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const sorted = [...rows].sort((a, b) => (parseLocalDate(a.date) || 0) - (parseLocalDate(b.date) || 0));
  const stock_actuel = parseFloat(sorted[0]?.stock) || 0;
  const stock_mini   = parseFloat(sorted[0]?.['stock mini']) || 0;
  const prix         = parseFloat(sorted[0]?.['Prix unitaire']) || 0;

  const conso30j = sorted.reduce((sum, r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return sum;
    if (d >= now && d <= in30) {
      const dc = parseFloat(r['depart client']);
      if (!isNaN(dc) && dc > 0) return sum + dc;
    }
    return sum;
  }, 0);

  const moisMap = {};
  sorted.forEach((r) => {
    const d = parseLocalDate(r.date);
    if (!d || isNaN(d)) return;
    const key = getMonthKey(d);
    if (!moisMap[key]) moisMap[key] = { analyses: [], stockProjete: null, stockMin: null, departClient: 0 };
    const a = (r['Analyse'] || '').trim().toLowerCase();
    if (a) moisMap[key].analyses.push(a);
    const sp = parseFloat(r['stock projete']);
    if (!isNaN(sp)) {
      moisMap[key].stockProjete = sp;
      if (moisMap[key].stockMin === null || sp < moisMap[key].stockMin) {
        moisMap[key].stockMin = sp;
      }
    }
    const dc = parseFloat(r['depart client']);
    if (!isNaN(dc)) moisMap[key].departClient += dc;
  });

  const getStatut = (stock, analyses) => {
    if (stock < 0) return 'rupture';
    if (stock === 0 && analyses.some(a => a === 'rupture sèche')) return 'rupture sèche';
    if (conso30j > 0) {
      if (stock > RATIO_FORT   * conso30j) return 'stock fort';
      if (stock < RATIO_FAIBLE * conso30j) return 'stock faible';
    } else {
      if (stock > STOCK_FORT_ABS_FALLBACK)   return 'stock fort';
      if (stock <= STOCK_FAIBLE_ABS_FALLBACK && stock > 0) return 'stock faible';
    }
    return 'ok';
  };

  const statutsMois = {};
  const ruptureInfraMensuelle = {};
  let dernierStatutConnu = null;
  for (let i = 0; i <= 11; i++) {
    const key = getMonthPeriod(i);
    const m = moisMap[key];
    if (!m || m.stockProjete == null) { statutsMois[i] = dernierStatutConnu; continue; }
    const s = getStatut(m.stockProjete, m.analyses);
    dernierStatutConnu = s;
    statutsMois[i] = s;
    if (m.stockMin !== null && m.stockMin < 0) {
      ruptureInfraMensuelle[i] = { stockMin: m.stockMin, stockFin: m.stockProjete };
    }
  }

  const stockM   = moisMap[getMonthPeriod(0)]?.stockProjete ?? null;
  const stockM6  = moisMap[getMonthPeriod(6)]?.stockProjete ?? null;
  const stockM12 = (() => {
    const d = moisMap[getMonthPeriod(11)]?.stockProjete;
    if (d != null) return d;
    for (let i = 10; i >= 0; i--) { const v = moisMap[getMonthPeriod(i)]?.stockProjete; if (v != null) return v; }
    return null;
  })();

  const valeurStock = stock_actuel * prix;
  const valeurM12   = stockM12 != null ? stockM12 * prix : null;
  let pctEvol = null, tendance = 'stable';
  if (valeurStock !== 0 && valeurM12 != null) {
    pctEvol = Math.round(((valeurM12 - valeurStock) / Math.abs(valeurStock)) * 100);
    if (pctEvol > 30) tendance = 'hausse';
    else if (pctEvol < -30) tendance = 'baisse';
  }

  const allStatuts = Object.values(statutsMois).filter(Boolean);
  let risque = 'ok';
  if (allStatuts.includes('rupture'))        risque = 'rupture';
  else if (allStatuts.includes('rupture sèche')) risque = 'rupture sèche';
  else if (allStatuts.includes('stock faible'))  risque = 'stock faible';
  else if (allStatuts.filter(s => s === 'stock fort').length >= 6) risque = 'stock fort';

  return { stock_actuel, stock_mini, conso30j, prix, stockM, stockM6, stockM12, pctEvol, tendance, risque, statutsMois, valeurStock, valeurM12, ruptureInfraMensuelle };
};

// ─── Composant principal ──────────────────────────────────────────────────────
const Stockprojete = () => {
  const [rawData, setRawData]           = useState([]);
  const [recalageData, setRecalageData] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [activeFilter, setActiveFilter] = useState('tous');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchText, setSearchText]     = useState('');
  const [sortCol, setSortCol]           = useState('gainTotal');
  const [sortDir, setSortDir]           = useState('desc');
  const [granularity, setGranularity]   = useState('mois');

  useEffect(() => { document.title = 'MYSSA – Stock projeté'; }, []);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIcon = (col) => sortCol !== col ? ' ↕' : sortDir === 'asc' ? ' ↑' : ' ↓';

  useEffect(() => {
    Promise.all([
      fetch(`${API}/donnee_histogramme`).then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); }),
      fetch(`${API}/recalage_cmde_frn`).then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); }),
    ])
      .then(([histo, recalage]) => {
        setRawData(histo);
        setRecalageData(recalage);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const recalageByItem = useMemo(() => {
    const idx = {};
    recalageData.forEach(r => {
      const item = (r.Item || '').trim();
      if (!item) return;
      if (!idx[item]) idx[item] = [];
      idx[item].push(r);
    });
    return idx;
  }, [recalageData]);

  const itemSummary = useMemo(() => {
    if (!rawData.length) return [];
    const map = {};
    rawData.forEach(row => {
      const item = (row.Item || '').trim();
      if (!item) return;
      if (!map[item]) map[item] = { item, description: (row.description || '').trim(), rows: [] };
      map[item].rows.push(row);
    });
    return Object.values(map).map(({ item, description, rows }) => {
      const stats = computeItemStats(rows);
      const cmds  = recalageByItem[item] || [];
      const hasReport   = cmds.some(c => c.statut === 'REPORT');
      const hasDecalage = cmds.some(c => c.statut === 'inf +2 semaines');

      const gainAnnulation = cmds
        .filter(c => (c.statut || '').toUpperCase() === 'ANNULATION')
        .reduce((sum, c) => sum + (parseFloat(c['Quantité commandée']) || 0) * stats.prix, 0);

      const gainCashMoyen = (() => {
        if (!cmds.length) return 0;
        const sortedRows = [...rows].sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
        const simuRows   = buildSimulatedRows(sortedRows, cmds);
        const baseAgg    = aggregateRows(sortedRows, 'mois');
        const simuAgg    = aggregateRows(simuRows, 'mois');
        const simuMap    = {};
        simuAgg.forEach(r => { simuMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
        const ecarts = baseAgg.map(r => {
          const sb = parseFloat(r['stock projete']) || 0;
          const ss = simuMap[r._periodKey] ?? sb;
          return (sb - ss) * stats.prix;
        });
        return ecarts.length ? ecarts.reduce((s, v) => s + v, 0) / ecarts.length : 0;
      })();

      const gainTotal = gainAnnulation + Math.max(0, gainCashMoyen);

      return { item, description, rows, cmds, hasReport, hasDecalage, gainAnnulation, gainCashMoyen, gainTotal, ...stats };
    });
  }, [rawData, recalageByItem]);

  const kpis = useMemo(() => ({
    ruptures:        itemSummary.filter(i => i.risque === 'rupture' || i.risque === 'rupture sèche').length,
    stockFaible:     itemSummary.filter(i => i.risque === 'stock faible').length,
    stockFort:       itemSummary.filter(i => i.risque === 'stock fort').length,
    encoursLibere:   itemSummary.reduce((a, b) => a + b.gainAnnulation, 0),
    optimTresorerie: itemSummary.reduce((a, b) => a + Math.max(0, b.gainCashMoyen), 0),
    valeur:          itemSummary.reduce((a, b) => a + b.valeurStock, 0),
  }), [itemSummary]);

  // ─── Filtres ──────────────────────────────────────────────────────────────
  const filteredItems = useMemo(() => itemSummary.filter(i => {
    const matchSearch = i.item.toLowerCase().includes(searchText.toLowerCase()) ||
      i.description.toLowerCase().includes(searchText.toLowerCase());
    if (!matchSearch) return false;

    // ── Exclure les items sans aucune activité ────────────────────────────
    // Un item est masqué uniquement si toutes ces conditions sont vraies :
    // stock = 0, aucune conso client, aucune livraison frn, stock projeté
    // partout à 0 ET aucune commande fournisseur en cours.
    const hasStock        = i.stock_actuel !== 0;
    const hasConsoClient  = i.rows.some(r => (parseFloat(r['depart client']) || 0) !== 0);
    const hasLivraisonFrn = i.rows.some(r => (parseFloat(r['livraison fournisseur']) || 0) !== 0);
    const hasStockProjete = i.rows.some(r => (parseFloat(r['stock projete']) || 0) !== 0);
    const hasCmds         = i.cmds.length > 0;

    if (!hasStock && !hasConsoClient && !hasLivraisonFrn && !hasStockProjete && !hasCmds) return false;
    // ─────────────────────────────────────────────────────────────────────

    if (activeFilter === 'rupture')      return i.risque === 'rupture' || i.risque === 'rupture sèche';
    if (activeFilter === 'stock_fort')   return i.risque === 'stock fort';
    if (activeFilter === 'stock_faible') return i.risque === 'stock faible';
    if (activeFilter === 'sain')         return i.risque === 'ok';
    return true;
  }), [itemSummary, activeFilter, searchText]);

  const sortedItems = useMemo(() => {
    if (!sortCol) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      if (sortCol === 'pctEvol') {
        const va = a.pctEvol ?? (sortDir === 'asc' ? Infinity : -Infinity);
        const vb = b.pctEvol ?? (sortDir === 'asc' ? Infinity : -Infinity);
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      if (sortCol === 'tendance') {
        const order = { baisse: 2, stable: 1, hausse: 0 };
        return sortDir === 'asc' ? (order[a.tendance]??1) - (order[b.tendance]??1) : (order[b.tendance]??1) - (order[a.tendance]??1);
      }
      if (sortCol === 'cmds') {
        return sortDir === 'asc' ? a.cmds.length - b.cmds.length : b.cmds.length - a.cmds.length;
      }
      if (sortCol === 'valeurM11') {
        const va = (a.stockM12 ?? -Infinity) * a.prix;
        const vb = (b.stockM12 ?? -Infinity) * b.prix;
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      if (sortCol === 'gainTotal') {
        return sortDir === 'asc' ? a.gainTotal - b.gainTotal : b.gainTotal - a.gainTotal;
      }
      return 0;
    });
  }, [filteredItems, sortCol, sortDir]);

  const modalChartData = useMemo(() => {
    if (!selectedItem) return null;
    const it = itemSummary.find(i => i.item === selectedItem);
    if (!it) return null;
    const rows = [...it.rows].sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
    return buildChartData(rows, granularity);
  }, [selectedItem, itemSummary, granularity]);

  const card = { background: '#FFFFFF', border: '0.5px solid #E5E7EB', borderRadius: 10, padding: '1rem 1.25rem' };
  const th   = { fontSize: 11, fontWeight: 600, color: '#6B7280', textAlign: 'center', padding: '8px 6px', borderBottom: '2px solid #0D2140', whiteSpace: 'nowrap', background: '#F8FAFC', letterSpacing: '0.04em', textTransform: 'uppercase' };
  const thL  = { ...th, textAlign: 'left' };
  const td   = { padding: '7px 6px', borderBottom: '0.5px solid #F3F4F6', verticalAlign: 'middle', textAlign: 'center', fontSize: 13 };
  const tdL  = { ...td, textAlign: 'left' };

  const modalSimuChartData = useMemo(() => {
    if (!selectedItem) return null;
    const it = itemSummary.find(i => i.item === selectedItem);
    if (!it || !it.cmds.length) return null;
    const rows = [...it.rows].sort((a, b) => (parseLocalDate(a.date)||0) - (parseLocalDate(b.date)||0));
    const simuRows = buildSimulatedRows(rows, it.cmds);
    const base = buildChartData(rows, granularity);

    const baseAggForChart = aggregateRows(rows, granularity);
    const simuAggForChart = aggregateRows(simuRows, granularity);
    const simuMapForChart = {};
    simuAggForChart.forEach(r => { simuMapForChart[r._periodKey] = parseFloat(r['stock projete']) || 0; });
    const simuDataAligned = baseAggForChart.map(r => {
      const stockBase = parseFloat(r['stock projete']) || 0;
      const stockSimu = simuMapForChart[r._periodKey] ?? stockBase;
      return Math.round(stockSimu);
    });

    const gainAnnulation = it.cmds
      .filter(c => (c.statut || '').toUpperCase() === 'ANNULATION')
      .reduce((sum, c) => sum + (parseFloat(c['Quantité commandée']) || 0) * it.prix, 0);

    const baseAggMois = aggregateRows(rows, 'mois');
    const simuAggMois = aggregateRows(simuRows, 'mois');
    const simuStockMap = {};
    simuAggMois.forEach(r => { simuStockMap[r._periodKey] = parseFloat(r['stock projete']) || 0; });
    const ecarts = baseAggMois.map(r => {
      const stockBase = parseFloat(r['stock projete']) || 0;
      const stockSimu = simuStockMap[r._periodKey] ?? stockBase;
      return (stockBase - stockSimu) * it.prix;
    }).filter(v => !isNaN(v));
    const gainCashMoyen = ecarts.length > 0
      ? ecarts.reduce((s, v) => s + v, 0) / ecarts.length
      : 0;

    return {
      chartData: {
        ...base,
        datasets: [
          ...base.datasets,
          {
            label: 'Stock projeté (après reports/annulations)',
            type: 'line',
            data: simuDataAligned,
            borderColor: '#1D9E75',
            backgroundColor: 'rgba(29,158,117,0.12)',
            borderWidth: 2.5,
            borderDash: [6, 3],
            fill: false,
            tension: 0.3,
            datalabels: { display: false },
          },
        ],
      },
      gainAnnulation,
      gainCashMoyen,
    };
  }, [selectedItem, itemSummary, granularity]);

  const activeChartData = modalSimuChartData?.chartData ?? modalChartData;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 12, color: '#6B7280', fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #E5E7EB', borderTopColor: '#0D2140', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      Chargement des stocks...
    </div>
  );
  if (error) return <div style={{ padding: '2rem', color: '#A32D2D' }}>Erreur : {error}</div>;

  const selectedItemData = selectedItem ? itemSummary.find(i => i.item === selectedItem) : null;

  return (
    <div style={{ padding: '1rem 0', fontFamily: "IBM Plex Sans, -apple-system, sans-serif", color: '#1A1A1A' }}>

      {/* ── KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Ruptures',     value: kpis.ruptures,    color: '#A32D2D', bg: '#FCEBEB', border: '#E24B4A', filterKey: 'rupture'      },
          { label: 'Stock faible', value: kpis.stockFaible, color: '#854F0B', bg: '#FAEEDA', border: '#EF9F27', filterKey: 'stock_faible'  },
          { label: 'Stock fort',   value: kpis.stockFort,   color: '#1565C0', bg: '#E8F4FD', border: '#1976D2', filterKey: 'stock_fort'    },
          {
            label: 'Gains identifiés', filterKey: 'tous', bg: '#F0FDF4', border: '#86EFAC',
            detail: [
              { label: 'Encours libéré',   value: fmt(kpis.encoursLibere),   color: '#15803D' },
              { label: 'Optim trésorerie', value: fmt(kpis.optimTresorerie), color: '#1D4ED8' },
            ],
          },
          { label: 'Valeur stock', value: fmt(kpis.valeur), color: '#374151', bg: '#F9FAFB', border: '#D1D5DB', filterKey: 'tous', small: true },
        ].map(k => (
          <div key={k.label} onClick={() => setActiveFilter(k.filterKey)} style={{
            background: activeFilter === k.filterKey ? k.bg : '#FFFFFF',
            border: `1.5px solid ${activeFilter === k.filterKey ? k.border : '#E5E7EB'}`,
            borderRadius: 10, padding: '14px 16px', textAlign: k.detail ? 'left' : 'center', cursor: 'pointer',
            transition: 'all 0.15s', boxShadow: activeFilter === k.filterKey ? '0 2px 8px rgba(0,0,0,0.07)' : 'none',
          }}>
            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{k.label}</div>
            {k.detail ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {k.detail.map(d => (
                  <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#6B7280' }}>{d.label}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: d.color, fontFamily: 'IBM Plex Mono, monospace' }}>{d.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: k.small ? 18 : 28, fontWeight: 600, color: k.color, lineHeight: 1 }}>{k.value}</div>
            )}
          </div>
        ))}
      </div>

      {/* ── Recherche ── */}
      <div style={{ marginBottom: 12 }}>
        <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
          placeholder="Rechercher un article ou une description..."
          style={{ width: '100%', padding: '9px 14px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, outline: 'none', background: '#FAFAFA', boxSizing: 'border-box', fontFamily: 'IBM Plex Mono, monospace' }}
        />
      </div>

      {/* ── Tableau ── */}
      <div style={{ ...card, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '72vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: '#6B7280', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {filteredItems.length} article{filteredItems.length > 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ overflowY: 'auto', overflowX: 'auto', flex: 1 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...thL, position: 'sticky', top: 0, zIndex: 2, width: 120 }}>Référence</th>
                <th style={{ ...thL, position: 'sticky', top: 0, zIndex: 2, width: 160 }}>Description</th>
                <th style={{ ...th,  position: 'sticky', top: 0, zIndex: 2, width: 70 }}>Stock act.</th>
                {[0,1,2,3,4,5].map(i => (
                  <th key={i} style={{ ...th, position: 'sticky', top: 0, zIndex: 2, width: 72 }}>
                    {i === 0 ? 'M' : `M+${i}`}
                    <div style={{ fontSize: 10, fontWeight: 400, color: '#9CA3AF' }}>{getMoisLabel(i)}</div>
                  </th>
                ))}
                <th onClick={() => handleSort('valeurM11')} style={{ ...th, position: 'sticky', top: 0, zIndex: 2, width: 90, cursor: 'pointer', color: sortCol === 'valeurM11' ? '#0D2140' : '#6B7280' }}>
                  M+11{sortIcon('valeurM11')}
                  <div style={{ fontSize: 10, fontWeight: 400, color: '#9CA3AF' }}>{getMoisLabel(11)}</div>
                  <div style={{ fontSize: 9, fontWeight: 500, color: '#1976D2', textTransform: 'none', letterSpacing: 0 }}>valeur €</div>
                </th>
                <th onClick={() => handleSort('pctEvol')} style={{ ...th, position: 'sticky', top: 0, zIndex: 2, width: 72, cursor: 'pointer' }}>% évol.{sortIcon('pctEvol')}</th>
                <th style={{ ...th, position: 'sticky', top: 0, zIndex: 2, width: 90, color: '#854F0B' }}>
                  Décalage
                  <div style={{ fontSize: 9, fontWeight: 400, color: '#9CA3AF', textTransform: 'none' }}>si stock fort</div>
                </th>
                <th style={{ ...th, position: 'sticky', top: 0, zIndex: 2, width: 100, color: '#15803D' }}>
                  💰 Encours libéré
                  <div style={{ fontSize: 9, fontWeight: 400, color: '#9CA3AF', textTransform: 'none' }}>annulations</div>
                </th>
                <th style={{ ...th, position: 'sticky', top: 0, zIndex: 2, width: 100, color: '#1D4ED8' }}>
                  📉 Optim trésorerie
                  <div style={{ fontSize: 9, fontWeight: 400, color: '#9CA3AF', textTransform: 'none' }}>éco. mensuelle</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map(it => {
                const isSelected   = selectedItem === it.item;
                const showDecalage = it.risque === 'stock fort' && it.cmds.length > 0;
                const { gainAnnulation, gainCashMoyen } = it;

                return (
                  <tr key={it.item} onClick={() => setSelectedItem(isSelected ? null : it.item)}
                    style={{ cursor: 'pointer', background: isSelected ? '#EFF6FF' : 'transparent', transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ ...tdL, fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600, color: '#0D2140' }}>{it.item}</td>
                    <td style={{ ...tdL, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#4B5563', fontSize: 12 }}>{it.description}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{fmtQty(it.stock_actuel)}</td>
                    {[0,1,2,3,4,5].map(i => {
                      const s = it.statutsMois[i];
                      const ruptureInfo = it.ruptureInfraMensuelle?.[i] ?? null;
                      return (
                        <td key={i} style={{ ...td, verticalAlign: 'middle' }}>
                          {s ? <MoisBadge statut={s} ruptureInfo={ruptureInfo} /> : <span style={{ color: '#E5E7EB' }}>—</span>}
                        </td>
                      );
                    })}
                    <td style={{ ...td, fontWeight: 600, color: it.stockM12 != null && it.stockM12 < 0 ? '#A32D2D' : '#111827', verticalAlign: 'middle' }}>
                      {it.stockM12 != null ? (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <span>{fmtQty(Math.round(it.stockM12))}</span>
                          <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 400 }}>{fmt(Math.round(it.stockM12) * it.prix)}</span>
                          {it.ruptureInfraMensuelle?.[11] && (
                            <span style={{ fontSize: 10, fontWeight: 700, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 6, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                              ⚠ {fmtQty(Math.round(it.ruptureInfraMensuelle[11].stockMin))}
                            </span>
                          )}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ ...td, fontWeight: 600, color: it.pctEvol == null ? '#9CA3AF' : it.pctEvol >= 0 ? '#0F6E56' : '#A32D2D' }}>
                      {it.pctEvol != null ? `${it.pctEvol > 0 ? '+' : ''}${it.pctEvol}%` : '—'}
                    </td>
                    <td style={td}>
                      {showDecalage ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#FAEEDA', color: '#854F0B' }}>
                          ⟳ Report
                        </span>
                      ) : it.risque === 'stock fort' ? (
                        <span style={{ color: '#9CA3AF', fontSize: 11 }}>Pas de cmde</span>
                      ) : (
                        <span style={{ color: '#E5E7EB', fontSize: 11 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {gainAnnulation > 0
                        ? <span style={{ color: '#15803D', fontSize: 12 }}>{fmt(gainAnnulation)}</span>
                        : <span style={{ color: '#E5E7EB' }}>—</span>}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {gainCashMoyen > 0
                        ? <span style={{ color: '#1D4ED8', fontSize: 12 }}>{fmt(gainCashMoyen)}</span>
                        : <span style={{ color: '#E5E7EB' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modale graphique ── */}
      {selectedItem && modalChartData && selectedItemData && (
        <>
          <div onClick={() => setSelectedItem(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(13,33,64,0.5)', zIndex: 1000 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 'min(1020px, 96vw)', maxHeight: '94vh', overflowY: 'auto',
            background: '#FFFFFF', border: '0.5px solid #E5E7EB', borderRadius: 14,
            padding: '1.75rem', zIndex: 1001, boxShadow: '0 16px 48px rgba(13,33,64,0.22)',
          }}>
            {/* Header modale */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 17, fontWeight: 600, color: '#0D2140' }}>{selectedItem}</span>
                  <StatusBadge statut={selectedItemData.risque} />
                </div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>{selectedItemData.description}</div>
              </div>
              <button onClick={() => setSelectedItem(null)} style={{
                background: '#F3F4F6', border: 'none', cursor: 'pointer', color: '#6B7280',
                fontSize: 16, width: 32, height: 32, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>✕</button>
            </div>

            {/* Métriques rapides */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Stock actuel',       value: fmtQty(selectedItemData.stock_actuel),                                                             color: '#0D2140' },
                { label: 'Valeur stock actuel',value: fmt(selectedItemData.valeurStock),                                                                  color: '#374151' },
                { label: 'Stock projeté M+11', value: selectedItemData.stockM12 != null ? fmtQty(Math.round(selectedItemData.stockM12)) : '—',           color: selectedItemData.stockM12 != null && selectedItemData.stockM12 < 0 ? '#A32D2D' : '#1565C0' },
                { label: 'Valeur stock M+11',  value: selectedItemData.valeurM12 != null ? fmt(selectedItemData.valeurM12) : '—',                         color: selectedItemData.valeurM12 != null && selectedItemData.valeurM12 < 0 ? '#A32D2D' : '#374151' },
              ].map(m => (
                <div key={m.label} style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px', border: '0.5px solid #E5E7EB' }}>
                  <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{m.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: m.color, fontFamily: 'IBM Plex Mono, monospace' }}>{m.value}</div>
                </div>
              ))}
            </div>

            {/* Gains financiers */}
            {modalSimuChartData && (modalSimuChartData.gainAnnulation > 0 || modalSimuChartData.gainCashMoyen > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: modalSimuChartData.gainAnnulation > 0 && modalSimuChartData.gainCashMoyen > 0 ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 20 }}>
                {modalSimuChartData.gainAnnulation > 0 && (
                  <div style={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)', border: '1.5px solid #86EFAC', borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 18 }}>💰</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Encours fournisseur libéré</span>
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#15803D', fontFamily: 'IBM Plex Mono, monospace', marginBottom: 4 }}>
                      +{fmt(modalSimuChartData.gainAnnulation)}
                    </div>
                    <div style={{ fontSize: 11, color: '#4ADE80', fontWeight: 500 }}>
                      Commandes annulées — montant non décaissé
                    </div>
                  </div>
                )}
                {modalSimuChartData.gainCashMoyen > 0 && (
                  <div style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', border: '1.5px solid #93C5FD', borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 18 }}>📉</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Optimisation trésorerie</span>
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: '#1D4ED8', fontFamily: 'IBM Plex Mono, monospace', marginBottom: 4 }}>
                      +{fmt(modalSimuChartData.gainCashMoyen)}
                    </div>
                    <div style={{ fontSize: 11, color: '#60A5FA', fontWeight: 500 }}>
                      Économie moyenne mensuelle de trésorerie immobilisée
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Alerte rupture infra-mensuelle */}
            {Object.keys(selectedItemData.ruptureInfraMensuelle || {}).length > 0 && (
              <div style={{ background: '#FFF8EC', border: '1px solid #FCD34D', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  ⚠ Rupture infra-mensuelle détectée
                </div>
                <div style={{ fontSize: 12, color: '#78350F', marginBottom: 8 }}>
                  Le stock fin de mois apparaît positif (livraison fournisseur reçue), mais une rupture a été détectée en cours de mois :
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(selectedItemData.ruptureInfraMensuelle).map(([offset, info]) => (
                    <span key={offset} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8, background: '#FEF3C7', border: '1px solid #FCD34D', fontSize: 12, fontWeight: 500, color: '#92400E' }}>
                      <span style={{ color: '#DC2626', fontWeight: 700 }}>↓ {fmtQty(Math.round(info.stockMin))}</span>
                      <span style={{ color: '#9CA3AF' }}>→</span>
                      <span style={{ color: '#059669', fontWeight: 700 }}>↑ {fmtQty(Math.round(info.stockFin))}</span>
                      <span style={{ color: '#6B7280' }}>{getMoisLabel(Number(offset))}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Commandes fournisseur */}
            {selectedItemData.cmds.length > 0 && (
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  ⟳ Commandes fournisseur — décalage possible pour réduire le stock fort
                </div>
                {selectedItemData.cmds.some(c => c.statut !== 'ANNULATION') && (
                  <div style={{ fontSize: 11, color: '#1E40AF', background: '#DBEAFE', borderRadius: 6, padding: '5px 10px', marginBottom: 10 }}>
                    La courbe verte en pointillés représente le stock projeté après application des reports et annulations proposés.
                  </div>
                )}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#DBEAFE' }}>
                        {['Fournisseur', 'N° commande', 'Date livraison', 'Qté', 'Report proposé', 'Statut', 'Approvisionneur'].map(h => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#1E40AF', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItemData.cmds.map((c, i) => {
                        const fmtD = (val) => {
                          if (!val) return '—';
                          const d = parseLocalDate(val) || new Date(val);
                          if (!d || isNaN(d)) return val;
                          return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                        };

                        const statutLabel = (() => {
                          const statut = (c.statut || '').trim().toUpperCase();
                          if (statut === 'ANNULATION') return { label: 'Annulation', bg: '#FEE2E2', color: '#B91C1C' };
                          const origD   = parseLocalDate(c['Date de livraison']) || new Date(c['Date de livraison']);
                          const reportD = parseLocalDate(c['Report proposé'])    || new Date(c['Report proposé']);
                          if (!origD || !reportD || isNaN(origD) || isNaN(reportD)) {
                            return { label: c.statut || '—', bg: '#E8F4FD', color: '#1565C0' };
                          }
                          const diffJours = Math.round((reportD - origD) / (1000 * 60 * 60 * 24));
                          if (diffJours < 0)  return { label: `Avancement (${Math.abs(diffJours)}j)`, bg: '#EAF3DE', color: '#3B6D11' };
                          if (diffJours > 0)  return { label: `Report (${diffJours}j)`,               bg: '#FFF3CD', color: '#856404' };
                          return { label: 'Inchangé', bg: '#F3F4F6', color: '#6B7280' };
                        })();

                        return (
                          <tr key={i} style={{ borderBottom: '0.5px solid #BFDBFE', background: i % 2 === 0 ? '#F0F7FF' : '#FFFFFF', opacity: c.statut?.toUpperCase() === 'ANNULATION' ? 0.75 : 1 }}>
                            <td style={{ padding: '6px 10px', fontWeight: 500, color: '#1E40AF' }}>{c.Fournisseur}</td>
                            <td style={{ padding: '6px 10px', fontFamily: 'IBM Plex Mono, monospace', color: '#374151' }}>{c['Numero de commande']}</td>
                            <td style={{ padding: '6px 10px', color: '#374151' }}>{fmtD(c['Date de livraison'])}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'right', color: '#0D2140' }}>{fmtQty(c['Quantité commandée'])}</td>
                            <td style={{ padding: '6px 10px', color: c['Report proposé'] === 'ANNULATION' ? '#A32D2D' : '#854F0B', fontWeight: 500 }}>
                              {c.statut?.toUpperCase() === 'ANNULATION' ? <span style={{ color: '#B91C1C', fontWeight: 700 }}>—</span> : fmtD(c['Report proposé'])}
                            </td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: statutLabel.bg, color: statutLabel.color, whiteSpace: 'nowrap' }}>
                                {statutLabel.label}
                              </span>
                            </td>
                            <td style={{ padding: '6px 10px', color: '#6B7280', fontSize: 11 }}>{c.Approvisionneur}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sélecteur granularité */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {[['jour','Jour'],['semaine','Semaine'],['mois','Mois']].map(([k, label]) => (
                <button key={k} onClick={() => setGranularity(k)} style={{
                  padding: '5px 14px', borderRadius: 16, cursor: 'pointer', fontSize: 12, fontWeight: granularity === k ? 700 : 400,
                  border: `1.5px solid ${granularity === k ? '#0D2140' : '#D1D5DB'}`,
                  background: granularity === k ? '#0D2140' : '#FFFFFF',
                  color: granularity === k ? '#FFFFFF' : '#374151', transition: 'all 0.15s',
                }}>{label}</button>
              ))}
            </div>

            {/* Graphe */}
            <div style={{ width: '100%', height: 380 }}>
              <Line data={activeChartData} options={chartOptions} />
            </div>

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSelectedItem(null)} style={{
                padding: '7px 22px', borderRadius: 6, cursor: 'pointer',
                background: '#F3F4F6', border: '0.5px solid #D1D5DB', fontSize: 14, color: '#374151',
              }}>Fermer</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Stockprojete;